import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
  exportEventsCSV,
  exportEventsJSON,
  exportEventsPDF,
  getTrends
} from '../../../services/SystemLoggingService';
import { getSyncHistory } from '../../../services/RepositoryService';
import {
  SyncHistoryItem,
  SyncHistoryResponse
} from '../../../types/Repository.types';
import { SystemLoggingEvent } from '../../../types/SystemLogging.types';
import { AppDispatch, RootState } from '../../../store';
import {
  resetFilters,
  setDateRangeFilter,
  setEventTypeFilter,
  setPage,
  setSearchFilter,
  setSeverityFilter,
  setSorting,
  setTrendsError,
  setTrendsLoading,
  setTrendsSuccess,
  setUserFilter
} from '../../../store/slices/systemLoggingSlice';
import { EventsTable } from '../../tables/EventsTable/EventsTable';
import { SearchSection } from '../SearchSection/SearchSection';
import { EventsCard } from './components/EventsCard/EventsCard';
import { ExportDropdown } from './components/ExportDropdown/ExportDropdown';
import { MonitoringFiltersSection } from './components/MonitoringFiltersSection/MonitoringFiltersSection';
import { OverviewChartCard } from './components/OverviewChartCard/OverviewChartCard';
import { TrendsChart } from './components/TrendsChart/TrendsChart';
import { useAuthorization } from '../../../hooks/useAuthorization';
import { SideDrawer } from '../../ui/SideDrawer/SideDrawer';
import { formatEventType } from '../../../utils/EventFormatting.util';
import { getEventDetailsDrawerContent } from './EventDetailsDrawerContent';
import { SupportedChangeImpact } from '../../ui/ChangeImpactBadge/ChangeImpactBadge';

function mapSyncHistoryToEvent(
  item: SyncHistoryItem,
  aiSystemId: string
): SystemLoggingEvent {
  return {
    id: item.id,
    aiSystemId,
    eventType: item.eventType,
    severity: 'INFO',
    changeImpact: item.severity as SystemLoggingEvent['changeImpact'],
    message: item.eventDescription,
    repositoryName: item.repositoryName,
    githubUserName: item.eventUser,
    createdAt: item.startedAt
  };
}

export function MonitoringAuditLogging() {
  const EVENTS_PAGE_SIZE = 10;
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { can, roleResolved } = useAuthorization();
  const hasOpenedEventFromNavigation = useRef(false);

  const { filters, pagination, sorting } = useSelector(
    (state: RootState) => state.systemLogging
  );

  const [syncItems, setSyncItems] = useState<SyncHistoryItem[]>([]);
  const [syncHistoryMeta, setSyncHistoryMeta] =
    useState<SyncHistoryResponse | null>(null);
  const [syncHistoryLoading, setSyncHistoryLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<SystemLoggingEvent | null>(
    null
  );
  const [openSideDrawer, setOpenSideDrawer] = useState(false);

  const canManageMonitoring =
    roleResolved && can('aiSystems.monitoring.manage');

  const navigationEventId =
    (location.state as { openEventId?: string } | null)?.openEventId ?? null;

  const paginatedEvents: SystemLoggingEvent[] = useMemo(() => {
    return syncItems.map((item) => mapSyncHistoryToEvent(item, id || ''));
  }, [syncItems, id]);

  const hasMore = syncHistoryMeta?.hasMore ?? false;

  const eventTypeOptions = useMemo(() => {
    return syncHistoryMeta?.filters.eventTypes ?? [];
  }, [syncHistoryMeta]);

  const severityOptions = useMemo(() => {
    if (!syncHistoryMeta) return [];
    return syncHistoryMeta.filters.severities.map((s) => {
      if (typeof s === 'object' && s !== null) return s;
      return { value: s, label: s.charAt(0) + s.slice(1).toLowerCase() };
    });
  }, [syncHistoryMeta]);

  const userOptions = useMemo(() => {
    if (!syncHistoryMeta) return [];
    return syncHistoryMeta.filters.users.map((u) => {
      if (typeof u === 'object' && u !== null) return u;
      return { value: u, label: u };
    });
  }, [syncHistoryMeta]);

  const handleOpenEventDrawer = (event: SystemLoggingEvent) => {
    setSelectedEvent(event);
    setOpenSideDrawer(true);
  };

  const handleEventImpactUpdated = (
    eventId: string,
    impact: SupportedChangeImpact
  ) => {
    setSyncItems((prev) =>
      prev.map((item) =>
        item.id === eventId ? { ...item, severity: impact } : item
      )
    );

    setSelectedEvent((prev) =>
      prev && prev.id === eventId ? { ...prev, changeImpact: impact } : prev
    );
  };

  const handleCloseEventDrawer = () => {
    setOpenSideDrawer(false);
    setSelectedEvent(null);
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const handleSort = (sortKey: string) => {
    if (sorting.sortBy === sortKey) {
      dispatch(
        setSorting({
          sortBy: sortKey,
          sortOrder: sorting.sortOrder === 'asc' ? 'desc' : 'asc'
        })
      );
    } else {
      dispatch(setSorting({ sortBy: sortKey, sortOrder: 'asc' }));
    }
  };

  const handleLoadMore = async () => {
    if (syncHistoryLoading || !hasMore || !id) return;

    setSyncHistoryLoading(true);
    try {
      const data = await getSyncHistory({
        aiSystemId: id,
        limit: EVENTS_PAGE_SIZE,
        offset: syncItems.length,
        keyword: filters.search || undefined,
        eventType: filters.eventType.length > 0 ? filters.eventType : undefined,
        severity: filters.severity.length > 0 ? filters.severity : undefined,
        user: filters.user.length > 0 ? filters.user : undefined,
        dateFrom: filters.dateRange?.startDate,
        dateTo: filters.dateRange?.endDate,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder
      });

      setSyncItems((prev) => [...prev, ...data.data]);
      setSyncHistoryMeta(data);
      dispatch(setPage(pagination.page + 1));
    } catch (error) {
      console.error('Failed to load more sync history:', error);
    } finally {
      setSyncHistoryLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'json' | 'pdf') => {
    if (!id) return;

    try {
      const exportParams = {
        aiSystemId: id,
        search: filters.search || undefined,
        eventType:
          filters.eventType.length > 0 ? filters.eventType[0] : undefined,
        changeImpact:
          filters.severity.length > 0 ? filters.severity[0] : undefined,
        userId: filters.user.length > 0 ? filters.user[0] : undefined,
        startDate: filters.dateRange?.startDate,
        endDate: filters.dateRange?.endDate
      };

      let blob: Blob;
      let fileExtension: string;

      switch (format) {
        case 'csv':
          blob = await exportEventsCSV(exportParams);
          fileExtension = 'csv';
          break;
        case 'json':
          blob = await exportEventsJSON(exportParams);
          fileExtension = 'json';
          break;
        case 'pdf':
          blob = await exportEventsPDF(exportParams);
          fileExtension = 'pdf';
          break;
        default:
          return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `system-logs-${
        new Date().toISOString().split('T')[0]
      }.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Failed to download ${format.toUpperCase()}:`, error);
    }
  };

  const handleRefreshTrends = async () => {
    if (!id) return;

    dispatch(setTrendsLoading(true));
    try {
      const data = await getTrends({
        aiSystemId: id,
        months: 12
      });
      dispatch(setTrendsSuccess(data));
    } catch (error) {
      console.error('Failed to fetch trends data:', error);
      dispatch(setTrendsError('Failed to fetch trends data'));
    }
  };

  useEffect(() => {
    const fetchTrendsData = async () => {
      if (!id) return;

      dispatch(setTrendsLoading(true));
      try {
        const data = await getTrends({
          aiSystemId: id,
          months: 12
        });
        dispatch(setTrendsSuccess(data));
      } catch (error) {
        console.error('Failed to fetch trends data:', error);
        dispatch(setTrendsError('Failed to fetch trends data'));
      }
    };

    fetchTrendsData();
  }, [id, dispatch]);

  useEffect(() => {
    const fetchFirstPage = async () => {
      if (!id) return;

      setSyncHistoryLoading(true);
      try {
        const data = await getSyncHistory({
          aiSystemId: id,
          limit: EVENTS_PAGE_SIZE,
          offset: 0,
          keyword: filters.search || undefined,
          eventType:
            filters.eventType.length > 0 ? filters.eventType : undefined,
          severity: filters.severity.length > 0 ? filters.severity : undefined,
          user: filters.user.length > 0 ? filters.user : undefined,
          dateFrom: filters.dateRange?.startDate,
          dateTo: filters.dateRange?.endDate,
          sortBy: sorting.sortBy,
          sortOrder: sorting.sortOrder
        });
        setSyncItems(data.data);
        setSyncHistoryMeta(data);
      } catch (error) {
        console.error('Failed to fetch sync history:', error);
      } finally {
        setSyncHistoryLoading(false);
      }
    };

    fetchFirstPage();
  }, [
    id,
    filters.search,
    filters.eventType,
    filters.severity,
    filters.user,
    filters.dateRange,
    sorting.sortBy,
    sorting.sortOrder
  ]);

  useEffect(() => {
    hasOpenedEventFromNavigation.current = false;
  }, [id, navigationEventId]);

  useEffect(() => {
    if (!navigationEventId || hasOpenedEventFromNavigation.current) {
      return;
    }

    const matchingEvent = paginatedEvents.find(
      (event) => event.id === navigationEventId
    );

    if (!matchingEvent) {
      return;
    }

    handleOpenEventDrawer(matchingEvent);
    hasOpenedEventFromNavigation.current = true;
  }, [navigationEventId, paginatedEvents]);

  return (
    <div className="flex flex-col gap-6 flex-1 max-h-[calc(100vh-300px)] overflow-y-auto p-1">
      <div className="flex items-center justify-between">
        <span className="font-inter font-medium text-base leading-6 text-[#27272A]">
          Logs
        </span>
        <ExportDropdown onExport={handleExport} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <EventsCard />
          <OverviewChartCard />
        </div>
        <div className="lg:col-span-3 h-full">
          <TrendsChart aiSystemId={id || ''} />
        </div>
      </div>

      <SearchSection
        searchValue={filters.search}
        onSearchChange={(value) => dispatch(setSearchFilter(value))}
        placeholder="Search by keyword"
      />

      <MonitoringFiltersSection
        selectedEventType={filters.eventType}
        onEventTypeChange={(value) => dispatch(setEventTypeFilter(value))}
        selectedSeverity={filters.severity}
        onSeverityChange={(value) => dispatch(setSeverityFilter(value))}
        selectedUser={filters.user}
        onUserChange={(value) => dispatch(setUserFilter(value))}
        selectedDateRange={filters.dateRange}
        onDateRangeChange={(value) => dispatch(setDateRangeFilter(value))}
        onResetFilters={handleResetFilters}
        eventTypeOptions={eventTypeOptions}
        eventTypesLoading={syncHistoryLoading}
        userOptions={userOptions}
        usersLoading={syncHistoryLoading}
        severityOptions={severityOptions}
      />

      <EventsTable
        events={paginatedEvents}
        loading={syncHistoryLoading}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        sortBy={sorting.sortBy || 'createdAt'}
        sortOrder={sorting.sortOrder || 'desc'}
        onSort={handleSort}
        onEventUpdated={handleRefreshTrends}
        onEventImpactUpdated={handleEventImpactUpdated}
        canEditSeverity={canManageMonitoring}
        onRowClick={handleOpenEventDrawer}
      />

      {selectedEvent && (
        <SideDrawer
          isOpen={openSideDrawer}
          onClose={handleCloseEventDrawer}
          title={formatEventType(selectedEvent.eventType)}
          listItems={getEventDetailsDrawerContent(selectedEvent)}
        />
      )}
    </div>
  );
}
